// Timezone helpers built on native Intl — no date library (TTT-14).
// Mirror of backend/src/lib/timezone.js (the two packages don't share a build), plus the
// display helpers the UI needs. Sessions store an absolute instant (startAt); the instructor
// enters wall-clock date+time in their own tz, and every surface renders an instant in a
// *named, labeled* tz — the viewer's own — so nobody silently sees browser-local time.

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const DEFAULT_TIMEZONE = 'Asia/Manila';

// Guard against a malformed tz reaching Intl (would throw a RangeError). Falls back to the default.
const tzValidCache = new Map();
function safeZone(tz) {
  if (typeof tz !== 'string' || !tz) return DEFAULT_TIMEZONE;
  if (!tzValidCache.has(tz)) {
    try { new Intl.DateTimeFormat('en-US', { timeZone: tz }); tzValidCache.set(tz, true); }
    catch { tzValidCache.set(tz, false); }
  }
  return tzValidCache.get(tz) ? tz : DEFAULT_TIMEZONE;
}

// The viewer's detected tz (overridable in the picker).
export function getBrowserTz() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

function tzOffsetMs(date, tz) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const p = Object.fromEntries(
    dtf.formatToParts(date).filter((x) => x.type !== 'literal').map((x) => [x.type, x.value])
  );
  const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return asUtc - date.getTime();
}

// Wall-clock → absolute instant. Twice a year a local time is not a single instant, so the
// two awkward cases are resolved explicitly rather than left to whatever falls out:
//
//   Ambiguous (clocks go back): 01:30 happens twice. We take the FIRST occurrence.
//   Non-existent (clocks go forward): 02:30 never happens. We shift FORWARD out of the gap,
//     so 02:30 becomes 03:30 — the instructor gets a later session, never an earlier one.
//
// This matches Luxon/Temporal's default ("compatible") behaviour. The previous two-pass
// version resolved a gap *backwards* (02:30 → 01:30), quietly scheduling an hour early.
//
// Probing a day either side brackets any transition; a candidate is real only if projecting
// it back into `tz` reproduces the requested wall time.
function wallToUtc(year, month, day, hour, minute, tz) {
  const asUtc = Date.UTC(year, month - 1, day, hour, minute);
  const DAY = 24 * 60 * 60 * 1000;
  const offBefore = tzOffsetMs(new Date(asUtc - DAY), tz);
  const offAfter = tzOffsetMs(new Date(asUtc + DAY), tz);

  const valid = [asUtc - offBefore, asUtc - offAfter]
    .filter((c) => tzOffsetMs(new Date(c), tz) === asUtc - c);

  // Normal time resolves once; ambiguous resolves twice → earliest wins.
  if (valid.length) return new Date(Math.min(...valid));

  // Gap: no instant maps to this wall time. Using the pre-transition offset lands just past
  // the gap, i.e. shifted forward by its width.
  return new Date(asUtc - offBefore);
}

const MINUTE_MS = 60 * 1000;

// The instant `weeks` weeks after `instant` at the same wall-clock time in `tz`. Across a clock
// change this is not a multiple of 7 × 24 hours: 18:00 stays 18:00. Duplicate uses it so the copy
// keeps the local start time. Mirror of addLocalWeeks in backend/src/lib/timezone.js.
export function addLocalWeeks(instant, weeks, tz) {
  const zone = safeZone(tz);
  const start = new Date(instant);
  const p = utcToZonedParts(start, zone);
  // Date.UTC rolls a day past the month end into the next month (and year).
  const day = new Date(Date.UTC(p.year, p.month - 1, p.day + 7 * weeks));
  const wall = wallToUtc(day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate(), p.hour, p.minute, zone);
  // Wall parts stop at minutes; carry any seconds over so a zero-week step returns the same instant.
  const subMinute = ((start.getTime() % MINUTE_MS) + MINUTE_MS) % MINUTE_MS;
  return new Date(wall.getTime() + subMinute);
}

// Calendar parts of an instant in `tz`: { year, month, day, weekday (0=Sun), dayName, time }.
export function utcToZonedParts(date, tz) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: safeZone(tz), hourCycle: 'h23', weekday: 'short',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
  const p = Object.fromEntries(
    dtf.formatToParts(date).filter((x) => x.type !== 'literal').map((x) => [x.type, x.value])
  );
  return {
    year: +p.year, month: +p.month, day: +p.day, hour: +p.hour, minute: +p.minute,
    weekday: DAYS.indexOf(p.weekday), dayName: p.weekday, time: `${p.hour}:${p.minute}`,
  };
}

// Interpret a form's "YYYY-MM-DD" + "HH:MM" as wall-clock time in `tz` (the instructor's own)
// and return the matching absolute UTC instant to send to the API. Returns null on bad input.
export function zonedDateTimeToUtc(dateStr, timeStr, tz) {
  const [y, m, d] = String(dateStr || '').split('-').map(Number);
  const [hh, mm] = String(timeStr || '').split(':').map(Number);
  if ([y, m, d, hh, mm].some((n) => Number.isNaN(n) || n === undefined)) return null;
  return wallToUtc(y, m, d, hh, mm, safeZone(tz));
}

// Current UTC offset of a zone as a label, e.g. "GMT+08:00" / "GMT-05:30" — the standard
// way pickers disambiguate timezones (Google Calendar / Teams style).
export function tzOffsetLabel(tz, date = new Date()) {
  const mins = Math.round(tzOffsetMs(date, safeZone(tz)) / 60000);
  const sign = mins < 0 ? '-' : '+';
  const abs = Math.abs(mins);
  return `GMT${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
}

// Same offset in minutes — used to sort timezone lists west→east.
export function tzOffsetMinutes(tz, date = new Date()) {
  return Math.round(tzOffsetMs(date, safeZone(tz)) / 60000);
}

// Format an instant in a named tz. Defaults to date + time + short tz label so the viewer
// always knows which timezone they're looking at. Returns '' for missing/invalid input.
export function formatInTz(instant, tz, opts) {
  if (!instant) return '';
  const d = new Date(instant);
  if (isNaN(d.getTime())) return '';
  const options = opts || {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  };
  return new Intl.DateTimeFormat('en-US', { timeZone: safeZone(tz), ...options }).format(d);
}

// True if the instant falls on "today" in the given tz (compares calendar day in that tz,
// not the browser's) — so an instructor's "classes today" counts by their own timezone.
export function isSameZonedDay(instant, tz, now = new Date()) {
  const a = utcToZonedParts(new Date(instant), tz);
  const b = utcToZonedParts(now, tz);
  return a.year === b.year && a.month === b.month && a.day === b.day;
}
